<template>
  <div>
  <b-row>
    <b-col cols="12" class="my-1">
      <div class="d-flex align-items-center justify-content-end">
        <b-button variant="primary" to="/customer/customer-add">
          <span class="text-nowrap">Add Customer</span>
        </b-button>
      </div>
    </b-col>
  </b-row>
    <b-row>
    <b-col cols="12" md="6" class="my-1">
      <b-form-group label="Select Columns:" class="mb-0">
        <b-form-checkbox-group 
          v-model="selectedColumns" 
          :options="columnOptions"
        ></b-form-checkbox-group>
      </b-form-group>
    </b-col>
  </b-row>
  
    <b-col cols="12" sm="3" class="my-1">
      <b-form-group class="mb-0">
        <label class="d-inline-block text-sm-left mr-50">Per page</label>
        <b-form-select id="perPageSelect" v-model="perPage" size="sm" :options="pageOptions"  />
      </b-form-group>
    </b-col>
  
    <b-col cols="12" sm="3" class="my-1">
    </b-col>
    <b-col cols="12" class="d-flex justify-content-end mb-2" style="position: relative; z-index: 1;">
      <b-button variant="outline-danger" @click="clearFilters">
        Clear Filters
      </b-button>
    </b-col>
    <b-row>
    <b-col cols="12">
      <b-table striped hover responsive :per-page="perPage" :current-page="currentPage" :items="filteredCustomers"
        :fields="dynamicFields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :sort-direction="sortDirection"
        @filtered="onFiltered">

        <!-- Filter for Customer Name -->
        <template #head(otherName)>
          <div class="d-flex flex-column">
            <span>Customer Name</span>
            <b-form-input v-model="columnFilters.otherName" placeholder="Filter by Customer Name" size="sm"
              @input="filterTable" />
          </div>
        </template>

        <!-- Filter for Email -->
        <template #head(email)>
          <div class="d-flex flex-column">
            <span>Email</span>
            <b-form-input v-model="columnFilters.email" placeholder="Filter by Email" size="sm" @input="filterTable" />
          </div>
        </template>

        <!-- Filter for Sales Person -->
        <template #head(salesPersonId)>
          <div class="d-flex flex-column">
            <span>Sales Person</span>
            <b-form-select v-model="columnFilters.salesPersonId" :options="salesPersonOptions"
              placeholder="Filter by Sales Person" size="sm" @change="filterTable" />
          </div>
        </template>

        <template #head(typeDesc)>
          <div class="d-flex flex-column">
            <span>Customer Type</span>
            <b-form-select v-model="columnFilters.typeDesc" :options="customerTypeOptions"
              placeholder="Filter by Customer Type" size="sm" @change="filterTable" />
          </div>
        </template>

        <template #head(fieldInstruction)>
          <div class="d-flex flex-column">
            <span>Category</span>
            <b-form-input v-model="columnFilters.fieldInstruction" placeholder="Filter by Category"
              size="sm" @input="filterTable" />
          </div>
        </template>

        <template #head(createdOn)>
          <div class="d-flex flex-column">
            <span>Created On</span>
            <DatePicker v-model="columnFilters.createdOnRange" range placeholder="Filter by Date Range" size="sm"
              @change="filterTable" :clearable="false" :input-attr="{ readonly: true }" @focus="openCalendar"
              class="small-datepicker" />
          </div>
        </template>


        <template #cell(companyName)="data">
          <b-badge v-if="data.item.companyName === null" variant="primary">
            Not a Company
          </b-badge>

          {{ data.item.companyName }}
        </template>
        <template #cell(surname)="data">
          <b-badge v-if="data.item.surname === null" variant="secondary">
            {{ data.item.companyName }}
          </b-badge>

          {{ data.item.surname }}
        </template>

        <template #cell(otherName)="data">
          <b-badge v-if="data.item.otherName === null" variant="secondary">
            {{ data.item.companyName }}
          </b-badge>

          {{ data.item.otherName }}  {{ data.item.surname }}
        </template>
        <template #cell(status)="data">
          <b-badge v-if="data.item.status === 1" variant="info"> Booked </b-badge>
          <b-badge v-else-if="data.item.status === 2" variant="primary">
            Available
          </b-badge>
          <b-badge v-else-if="data.item.status === 3" variant="warning">
            Maintainace
          </b-badge>
          <b-badge v-else-if="data.item.status === 4" variant="danger"> Broken </b-badge>
          <b-badge v-else variant="danger"> Not Available </b-badge>
        </template>
        <template #cell(salesPersonId)="data">
          <p v-if="data.item.salesPersonId === null">-</p>
          <p v-else>{{ getSalePersonName(data.item.salesPersonId) }}</p>
        </template>

        <template #cell(createdOn)="data">
          {{ formatDate(data.item.createdOn) }}
        </template>
        <template #cell(actions)="data">
          <b-button variant="primary" :to="getCustomerData(data.item.id)">
            <feather-icon icon="EyeIcon"  />
            <!-- <span>View</span> -->
          </b-button>
        </template>
      </b-table>
      <div class="d-flex justify-content-center mb-1">
        <b-spinner variant="success" label="Spinning" block v-if="loading"
          style="width: 3rem; height: 3rem"></b-spinner>
      </div>
    </b-col>

    <b-col cols="12">
      <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center" size="sm"
        class="my-0" />
      <b-form-group>
        Showing 
        {{ totalRows > 0 ? (perPage * (currentPage - 1) + 1) : 0 }} 
        to 
        {{ totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0 }} 
        of 
        {{ totalRows > 0 ? totalRows : 0 }} 
        entries
      </b-form-group>
    </b-col>
  </b-row>
</div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { mapActions, mapGetters } from "vuex";
import {
  BTable,
  BAvatar,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BSpinner,
  BDropdown,
  BDropdownItem,
  BFormCheckboxGroup,
} from "bootstrap-vue";

export default {
  components: {
    BTable,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BDropdown,
    BDropdownItem,
    BSpinner,
    DatePicker,
    BFormCheckboxGroup
  },
  data() {
    return {
      companyName: "",
      status: "",
      perPage: 5,
      customerData: [],
      pageOptions: [3, 5, 10],
      totalRows: 0,
      currentPage: 1,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      infoModal: {
        id: "info-modal",
        title: "",
        content: "",
      },
      columnFilters: {
        otherName: "",
        email: null,
        salesPersonId: null,
        typeDesc: null,
        fieldInstruction: "",
        createdOnRange: null,
      },
      customerTypeOptions: [
        { value: null, text: "All" },
        { value: "COMPANY", text: "COMPANY" },
        { value: "INDIVIDUAL", text: "INDIVIDUAL" },
      ],
      selectedColumns: ['otherName', 'typeDesc', 'email', 'salesPersonId', 'createdOn', 'fieldInstruction', 'actions'], // Default selected columns
      columnOptions: [
        
        { value: 'typeDesc', text: 'Customer Type' },
        { value: 'email', text: 'Email' },
        { value: 'salesPersonId', text: 'Sales Person' },
        { value: 'createdOn', text: 'Created On' },
        { value: 'fieldInstruction', text: 'Category' },
        
      ],

      fields: [
        { key: "otherName", label: "Customer Name", sortable: true },

        { key: "typeDesc", label: "Customer Type" },
        { key: "email", label: "Email", sortable: true },
        { key: "salesPersonId", label: "Sales Person" },
        { key: "createdOn", label: "Created On", sortable: "true" },
         { key: "fieldInstruction", label: "Category", sortable: "true" },
         { key: "actions" },
      ],
      status: [
        {
          1: "Current",
          2: "Professional",
          3: "Rejected",
          4: "Resigned",
          5: "Applied",
        },
        {
          1: "light-primary",
          2: "light-success",
          3: "light-danger",
          4: "light-warning",
          5: "light-info",
        },
      ],
    };
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter((f) => f.sortable)
        .map((f) => ({ text: f.label, value: f.key }));
    },
    ...mapGetters("salesPersonModule", {
      SalesPersonList: "salesperson",
      loading: "loading",
    }),
    ...mapGetters("customerModule", {
      customerList: "customers",
      loading: "loading",
    }),
    // Options for Sales Person dropdown filter
    salesPersonOptions() {
      return [{ value: null, text: "All" }, ...this.SalesPersonList.map(person => ({ value: person.id, text: `${person.firstName} ${person.lastName}` }))];
    },
    dynamicFields() {
      return this.fields.filter(field => this.selectedColumns.includes(field.key));
    },
    filteredCustomers() {
      const filteredData = this.customerList.filter((customer) => {
        const matchesCustomerName = !this.columnFilters.otherName || (customer.otherName && customer.otherName.toLowerCase().includes(this.columnFilters.otherName.toLowerCase()));
        const matchesEmail = !this.columnFilters.email || (customer.email && customer.email.toLowerCase().includes(this.columnFilters.email.toLowerCase()));
        const matchesSalesPerson = !this.columnFilters.salesPersonId || customer.salesPersonId === this.columnFilters.salesPersonId;
        const matchesCustomerType = !this.columnFilters.typeDesc || customer.typeDesc === this.columnFilters.typeDesc;
        const matchesSpecialInstructions = !this.columnFilters.fieldInstruction || (customer.fieldInstruction && customer.fieldInstruction.toLowerCase().includes(this.columnFilters.fieldInstruction.toLowerCase()));

        // Check if the customer's createdOn date falls within the selected range
        const matchesCreatedOnRange = !this.columnFilters.createdOnRange || (
          customer.createdOn &&
          new Date(customer.createdOn) >= new Date(this.columnFilters.createdOnRange[0]) &&
          new Date(customer.createdOn) <= new Date(this.columnFilters.createdOnRange[1])
        );

        return matchesCustomerName && matchesEmail && matchesSalesPerson && matchesCustomerType && matchesSpecialInstructions && matchesCreatedOnRange;
      });

      this.totalRows = filteredData.length;
      return filteredData;
    },
    anyFilterActive() {
      const { otherName, email, salesPersonId, typeDesc, fieldInstruction, createdOnRange } = this.columnFilters;
      return (
        otherName || email || salesPersonId !== null || typeDesc !== null || fieldInstruction || createdOnRange !== null
      );
    },

  },
  mounted() {
    this.totalRows = this.customerList.length;
  },
  async mounted() {
    await this.getSalesPersonListAction();
    await this.getCustomersListAction()
      .then(() => {
        this.totalRows = this.customerList.length;
      })
      .catch((response) => {
        console.log(response);
      });
  },
  methods: {
    ...mapActions("customerModule", [
      "getCustomersListAction",
      "deleteCustomersAction",
      "updateCustomersAction",
    ]),

    ...mapActions("salesPersonModule", ["getSalesPersonListAction"]),

    formatDate(date) {
      if (!date || isNaN(new Date(date).getTime())) {
        return "";
      }

      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(date).toLocaleDateString("en-US", options);
    },
    getSalePersonName(id) {
      const salesPerson = this.SalesPersonList.find((s) => s.id === id);
      return salesPerson ? salesPerson.firstName + " " + salesPerson.lastName : "";
    },
    // getCustomerName(id) {
    //   const cust = this.customerList.find((s) => s.id === id);
    //   return cust ? cust.otherName + " " + cust.surname : "";
    // },

    getCustomerData(data) {
      return "/customer/customer-view/" + data;
    },

    getCustomerEditData(data) {
      return "/customer/customer-edit/" + data;
    },
    clearFilters() {
      // Reset each filter property individually to ensure reactivity
      this.columnFilters.otherName = "";
      this.columnFilters.email = null;
      this.columnFilters.salesPersonId = null;
      this.columnFilters.typeDesc = null;
      this.columnFilters.fieldInstruction = "";
      this.columnFilters.createdOnRange = null;

      // Reset pagination
      this.currentPage = 1;

      // Trigger re-computation of filtered items
      this.filterTable();
    },
    filterTable() {
      // Set `totalRows` based on the full customer list to reset the filtered results
      this.totalRows = this.customerList.length;

      // Force reactivity by re-setting the filteredCustomers array
      this.filteredCustomers = [...this.customerList];

      // Reset pagination to the first page
      this.currentPage = 1;
    },
    removeCustomer(id) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.deleteCustomersAction(id);
          this.$swal({
            icon: "success",
            title: "Deleted!",
            text: "Customer has been deleted",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
        } else if (result.dismiss === "cancel") {
          this.$swal({
            title: "Cancelled",
            text: "Customer not deleted:)",
            icon: "error",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
        }
      });
    },

    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`;
      this.infoModal.content = JSON.stringify(item, null, 2);
      this.$root.$emit("bv::show::modal", this.infoModal.id, button);
    },
    resetInfoModal() {
      this.infoModal.title = "";
      this.infoModal.content = "";
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
  },
  openCalendar(event) {
    event.target.blur();
    event.target.click();

    document.addEventListener('click', this.closeCalendar);
  },
  closeCalendar(event) {
    const datePicker = this.$refs.datePicker.$el;
    if (!datePicker.contains(event.target)) {
      this.$refs.datePicker.hideCalendar();

      document.removeEventListener('click', this.closeCalendar);
    }
  },
};
</script>

<style scoped>
.small-datepicker {
  width: 150px;
}
</style>
